import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Title,
  Tooltip,
  Slider,
  CloseButton,
  Badge,
  Stack,
  Divider,
} from "@mantine/core";
import { IconBuildingStore, IconCheck, IconInfoCircle, IconUsers } from "@tabler/icons-react";
import UsageProgressBar from "@/PageComponents/Subscription/UsageProgressBar";
import styles from "@/PageComponents/Subscription/UsageSummary.module.css";
import type { UseQueryResult } from "@tanstack/react-query";

import moment from "moment";

export interface RateCard {
  ItemCode: string;
  BaseRate: number;
  CurrentRate: number;
  CurrentUsage: number;
  EstimatedCurrentBillingAmount: number;
  NextRate: number;
  CurrentIncludedAmount: number;
  NextIncludedAmount: number;
  UsageType: number;
  DiscountPercentage?: number;
  DiscountEndDate?: string | null;
}

export interface UsageStats {
  CustomerID: string;
  RateCards: {
    SCUserStandard?: RateCard;
    SCStoreStandard?: RateCard;
    SCDataStandard?: RateCard;
    SCEmailStandard?: RateCard;
    [key: string]: RateCard | undefined;
  };
  CurrentPeriodStart: string;
  NextPeriodStart: string;
  EstimatedCurrentBillingAmountIncl: number;
  EstimatedCurrentBillingAmountExcl: number;
}

export interface SubscriptionInfoLike {
  IsCancelled: boolean;
}

export interface UsageSummaryProps {
  subscriptionInfo: SubscriptionInfoLike;
  usageStatsQuery: UseQueryResult<UsageStats>;
  isTrial?: boolean;
  title?: string;
  showUsageOnly?: boolean;
  showSubscriptionOnly?: boolean;
}

// helpers - storage formatting
const BYTES_IN_MB = 1024 * 1024;
const BYTES_IN_GB = 1024 * 1024 * 1024;

function formatStorage(bytes: number): string {
  if (bytes < BYTES_IN_GB) {
    const mb = bytes / BYTES_IN_MB;
    return `${Math.round(mb)}MB Used`;
  }
  const gb = bytes / BYTES_IN_GB;
  return `${gb.toFixed(2)}GB Used`;
}

function formatFreeStorageLabel(bytes: number): string {
  if (bytes < BYTES_IN_GB) {
    const mb = bytes / BYTES_IN_MB;
    return `${Math.round(mb)}MB Free`;
  }
  const gb = bytes / BYTES_IN_GB;
  return `${gb.toFixed(2)}GB Free`;
}

const isDiscountActive = (percentage: number | undefined, endDate: string | null | undefined): boolean => {
  if (!percentage || percentage <= 0) return false;
  if (!endDate) return true;
  return moment().isSameOrBefore(moment(endDate), "day");
};

const UsageSummary: React.FC<UsageSummaryProps> = ({
  subscriptionInfo,
  usageStatsQuery,
  isTrial = false,
  title = "Usage",
  showUsageOnly = false,
  showSubscriptionOnly = false,
}: any) => {
  const [storageUsedBytes, setStorageUsedBytes] = useState(0);
  const [emailsSent, setEmailsSent] = useState(0);
  const [stats, setStats] = useState<UsageStats | null>(null);

  useEffect(() => {
    if (usageStatsQuery.data) {
      setStats(usageStatsQuery.data);
      setStorageUsedBytes(
        usageStatsQuery.data.RateCards?.SCDataStandard?.CurrentUsage ?? 0,
      );
      setEmailsSent(
        usageStatsQuery.data.RateCards?.SCEmailStandard?.CurrentUsage ?? 0,
      );
    }
  }, [usageStatsQuery.data]);

  const showStore = (stats?.RateCards?.SCStoreStandard?.CurrentUsage ?? 0) > 1;
  const userRateCard = stats?.RateCards?.SCUserStandard;
  const storeRateCard = stats?.RateCards?.SCStoreStandard;
  const storageRateCard = stats?.RateCards?.SCDataStandard;
  const emailRateCard = stats?.RateCards?.SCEmailStandard;

  const users = userRateCard?.CurrentUsage ?? 0;
  const usersFreeTier =
    userRateCard?.CurrentIncludedAmount !== userRateCard?.NextIncludedAmount
      ? userRateCard?.NextIncludedAmount || 0
      : userRateCard?.CurrentIncludedAmount || 0;
  const billedUsers = Math.max(0, users - usersFreeTier);
  const userDiscountPercentageVal = isDiscountActive(userRateCard?.DiscountPercentage, userRateCard?.DiscountEndDate)
    ? (userRateCard?.DiscountPercentage ?? 0)
    : 0;
  const ratePerUser =
    userRateCard?.CurrentRate !== userRateCard?.NextRate
      ? (userRateCard?.NextRate ?? 0)
      : (userRateCard?.BaseRate ?? userRateCard?.CurrentRate ?? 0);
  const usersTotal = userRateCard?.EstimatedCurrentBillingAmount ?? 0;

  const stores = storeRateCard?.CurrentUsage ?? 0;
  const storesFreeTier =
    storeRateCard?.CurrentIncludedAmount !== storeRateCard?.NextIncludedAmount
      ? storeRateCard?.NextIncludedAmount || 0
      : (storeRateCard?.CurrentIncludedAmount || 1);
  const billedStores = Math.max(0, stores - storesFreeTier);
  const storeDiscountPercentageVal = isDiscountActive(storeRateCard?.DiscountPercentage, storeRateCard?.DiscountEndDate)
    ? (storeRateCard?.DiscountPercentage ?? 0)
    : 0;
  const ratePerStore =
    storeRateCard?.CurrentRate !== storeRateCard?.NextRate
      ? (storeRateCard?.NextRate ?? 0)
      : (storeRateCard?.BaseRate ?? storeRateCard?.CurrentRate ?? 0);
  const storesTotal = storeRateCard?.EstimatedCurrentBillingAmount ?? 0;

  const storageFreeTierBytes =
    storageRateCard?.CurrentIncludedAmount !==
    storageRateCard?.NextIncludedAmount
      ? storageRateCard?.NextIncludedAmount || 0
      : storageRateCard?.CurrentIncludedAmount || 0;
  const billedStorageBytes = Math.max(
    0,
    storageUsedBytes - storageFreeTierBytes,
  );
  const billedStorageGB = billedStorageBytes / BYTES_IN_GB;
  const storageDiscountPercentageVal = isDiscountActive(storageRateCard?.DiscountPercentage, storageRateCard?.DiscountEndDate)
    ? (storageRateCard?.DiscountPercentage ?? 0)
    : 0;
  const storageRate =
    storageRateCard?.CurrentRate !== storageRateCard?.NextRate
      ? (storageRateCard?.NextRate ?? 0) * BYTES_IN_GB
      : (storageRateCard?.BaseRate ?? storageRateCard?.CurrentRate ?? 0) * BYTES_IN_GB;
  const storagePctDiscountFactor = 1 - (storageDiscountPercentageVal / 100);
  const localEstimatedStorageCost = billedStorageGB * storageRate * storagePctDiscountFactor;
  const storageCost =
    storageRateCard?.EstimatedCurrentBillingAmount === 0
      ? localEstimatedStorageCost
      : (storageRateCard?.EstimatedCurrentBillingAmount ??
        localEstimatedStorageCost);

  const emailsFreeTier =
    emailRateCard?.CurrentIncludedAmount !== emailRateCard?.NextIncludedAmount
      ? emailRateCard?.NextIncludedAmount || 0
      : emailRateCard?.CurrentIncludedAmount || 0;
  const billedEmails = Math.max(0, emailsSent - emailsFreeTier);
  const emailDiscountPercentageVal = isDiscountActive(emailRateCard?.DiscountPercentage, emailRateCard?.DiscountEndDate)
    ? (emailRateCard?.DiscountPercentage ?? 0)
    : 0;
  const emailRate =
    emailRateCard?.CurrentRate !== emailRateCard?.NextRate
      ? (emailRateCard?.NextRate ?? 0)
      : (emailRateCard?.BaseRate ?? emailRateCard?.CurrentRate ?? 0);
  const emailPctDiscountFactor = 1 - (emailDiscountPercentageVal / 100);
  const localEstimatedEmailCost = billedEmails * emailRate * emailPctDiscountFactor;
  const emailCost =
    emailRateCard?.EstimatedCurrentBillingAmount === 0
      ? localEstimatedEmailCost
      : (emailRateCard?.EstimatedCurrentBillingAmount ??
        localEstimatedEmailCost);

  const usersLabel = useMemo(() => `${users} monthly users`, [users]);
  const storesLabel = useMemo(() => `${stores} monthly stores (${storesFreeTier} Free)`, [stores, storesFreeTier]);
  const storesRateLabel = useMemo(
    () => `R${ratePerStore.toFixed(2)} per additional store`,
    [ratePerStore],
  );
  const rateLabel = useMemo(
    () => `R${ratePerUser.toFixed(2)} per user`,
    [ratePerUser],
  );

  const storageMinMaxBytes = BYTES_IN_GB * 10;
  const emailsMinMax = 10000;

  // Pro‑rata calculations – client side only for display during trial
  const daysInMonth = useMemo(() => moment().daysInMonth(), []);
  const dayOfMonth = useMemo(() => moment().date(), []);
  const remainingDays = useMemo(() => Math.max(0, daysInMonth - dayOfMonth + 1), [daysInMonth, dayOfMonth]);
  // todo pro rata rate should use the prorata billing factor from the server (seen in subscriptionInfo)
  const prorationFactor = useMemo(() => (daysInMonth ? remainingDays / daysInMonth : 1), [remainingDays, daysInMonth]);

  // User totals (apply percentage discount first, then pro‑rata on trial)
  const baseUsersAmount = useMemo(() => billedUsers * ratePerUser, [billedUsers, ratePerUser]);
  const usersPctDiscountFactor = useMemo(() => 1 - ((userDiscountPercentageVal || 0) / 100), [userDiscountPercentageVal]);
  const usersAfterPct = useMemo(() => baseUsersAmount * usersPctDiscountFactor, [baseUsersAmount, usersPctDiscountFactor]);
  const usersProratedDiscount = useMemo(() => isTrial ? usersAfterPct * (1 - prorationFactor) : 0, [isTrial, usersAfterPct, prorationFactor]);
  const usersTotalCalc = useMemo(() => (isTrial ? usersAfterPct - usersProratedDiscount : usersAfterPct), [isTrial, usersAfterPct, usersProratedDiscount]);

  // Store totals (billable stores exclude the first free store)
  const baseStoresAmount = useMemo(() => billedStores * ratePerStore, [billedStores, ratePerStore]);
  const storesPctDiscountFactor = useMemo(() => 1 - ((storeDiscountPercentageVal || 0) / 100), [storeDiscountPercentageVal]);
  const storesAfterPct = useMemo(() => baseStoresAmount * storesPctDiscountFactor, [baseStoresAmount, storesPctDiscountFactor]);
  const storesProratedDiscount = useMemo(() => isTrial ? storesAfterPct * (1 - prorationFactor) : 0, [isTrial, storesAfterPct, prorationFactor]);
  const storesTotalCalc = useMemo(() => (isTrial ? storesAfterPct - storesProratedDiscount : storesAfterPct), [isTrial, storesAfterPct, storesProratedDiscount]);

  const estimatedTotalExclVAT = useMemo(() => usersTotalCalc + (showStore ? storesTotalCalc : 0), [usersTotalCalc, storesTotalCalc, showStore]);

  if (usageStatsQuery.isError) {
    return (
      <Box p="lg" bg="gray.0" style={{ borderRadius: 8 }}>
        <Text size="sm" c="yellow.7">
          Unable to load usage statistics at this time.
        </Text>
      </Box>
    );
  }

  if (usageStatsQuery.isLoading && !stats) {
    return (
      <Box p="lg" bg="gray.0" style={{ borderRadius: 8 }}>
        <Text size="sm" c="gray.6">
          Loading usage statistics...
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="start" gap={'xs'} align="center" mt={2} mb={12}>
        <Title order={5}>
          {title}
        </Title>
        {isTrial && showUsageOnly && (
            <Text size="sm" fw={700} c="green.6">
              Free while on trial
            </Text>
        )}
        { !showSubscriptionOnly &&
            <Flex align="center" gap={8} ml={'auto'}>
              <Text size="sm" c="gray.6">
                {moment().format("MMMM YYYY")}
              </Text>
            </Flex>
        }
      </Flex>

      {!showUsageOnly && (
        <>
          {/* User subscription */}
          {
            !showSubscriptionOnly &&
              <Flex align="center" justify="space-between" className={styles.header}>
                <Flex className={styles.infoTitle}>
                  <Text size={"sm"} fw={700}>
                    {title === "Subscribe" ? "User subscription" : (title === "Usage" && subscriptionInfo.IsCancelled ? "Subscribe" : "User subscription")}
                  </Text>
                </Flex>
              </Flex>
          }

          <Flex align="center" justify="space-between" className={styles.section}>
            <Flex align="center" gap={8}>
              <IconUsers size={22} color="var(--mantine-color-scBlue-6)" />
              <Flex direction="column">
                <Text size="sm" c="dark.9">
                  {usersLabel}
                </Text>
                <Text size="12px" c="gray.6">
                  {rateLabel}
                </Text>
              </Flex>
            </Flex>
          </Flex>

          {((userDiscountPercentageVal || 0) > 0 || (isTrial && baseUsersAmount > 0) || (billedUsers > 1)) && (
            <Box bg="gray.0" p="sm" mt="xs">
              <Stack gap={4}>
                {((userDiscountPercentageVal || 0) > 0 || (billedUsers > 1) || (isTrial && baseUsersAmount > 0)) && (
                  <Flex justify="space-between" align="center">
                    {(userDiscountPercentageVal || 0) > 0 ? (
                      <Badge
                        variant="light"
                        color="green.6"
                        c="green.9"
                        leftSection={<IconCheck size={12} />}
                        size="sm"
                        tt="none"
                        style={{ borderRadius: 4, height: 20, fontSize: 12 }}
                      >
                        {userDiscountPercentageVal}% discount applied{userRateCard?.DiscountEndDate ? ` until ${moment(userRateCard.DiscountEndDate).format("D MMM YYYY")}` : ""}
                      </Badge>
                    ) : (
                      <Box />
                    )}
                    <Text size="sm" fw={400} c="dark.6">
                      {`${billedUsers.toLocaleString()} × ${(ratePerUser * usersPctDiscountFactor).toFixed(2)} = `}
                      <Text span fw={700}>
                        R{usersAfterPct.toFixed(2)}
                      </Text>
                    </Text>
                  </Flex>
                )}

                {isTrial && baseUsersAmount > 0 && (
                  <>
                    <Flex justify="space-between" align="center">
                      <Badge
                        variant="light"
                        color="green.6"
                        c="green.9"
                        leftSection={<IconCheck size={12} />}
                        size="sm"
                        tt="none"
                        style={{ borderRadius: 4, height: 20, fontSize: 12 }}
                      >
                        Pro-rated for the remaining days this month
                      </Badge>
                      <Text size="sm" c="green.9" fw={700}>
                        − R{usersProratedDiscount.toFixed(2)}
                      </Text>
                    </Flex>
                    <Flex justify="end">
                      <Text size="sm" fw={700} c="dark.6">
                        R{usersTotalCalc.toFixed(2)}
                      </Text>
                    </Flex>
                  </>
                )}
              </Stack>
            </Box>
          )}

          {/* Stores */}
          {showStore && (
            <>
              <Box className={styles.divider} />
              <Box className={styles.section}>

                {
                  !showSubscriptionOnly &&
                    <Flex
                        align="center"
                        justify="space-between"
                        className={styles.header}
                    >
                      <Flex className={styles.infoTitle}>
                        <Text size={"sm"} fw={700}>
                          Stores
                        </Text>
                      </Flex>
                    </Flex>
                }

                <Flex
                  align="center"
                  justify="space-between"
                  className={styles.section}
                >
                  <Flex align="center" gap={8}>
                    <IconBuildingStore size={22} color="var(--mantine-color-scBlue-6)" />
                    <Flex direction="column">
                      <Text size="sm" c="dark.9">
                        {storesLabel}
                      </Text>
                      <Text size="12px" c="gray.6">
                        {storesRateLabel}
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>

                {((storeDiscountPercentageVal || 0) > 0 || (isTrial && baseStoresAmount > 0) || (billedStores > 0)) && (
                  <Box bg="gray.0" p="sm" mt="xs">
                    <Stack gap={4}>
                      {((storeDiscountPercentageVal || 0) > 0 || (billedStores > 0) || (isTrial && baseStoresAmount > 0)) && (
                        <Flex justify="space-between" align="center">
                          {(storeDiscountPercentageVal || 0) > 0 ? (
                            <Badge
                              variant="light"
                              color="green.6"
                              c="green.9"
                              leftSection={<IconCheck size={12} />}
                              size="sm"
                              tt="none"
                              style={{ borderRadius: 4, height: 20, fontSize: 12 }}
                            >
                              {storeDiscountPercentageVal}% discount applied{storeRateCard?.DiscountEndDate ? ` until ${moment(storeRateCard.DiscountEndDate).format("D MMM YYYY")}` : ""}
                            </Badge>
                          ) : (
                            <Box />
                          )}
                          <Text size="sm" fw={400} c="dark.6">
                            {`${billedStores.toLocaleString()} × ${(ratePerStore * storesPctDiscountFactor).toFixed(2)} = `}
                            <Text span fw={700}>
                              R{storesAfterPct.toFixed(2)}
                            </Text>
                          </Text>
                        </Flex>
                      )}

                      {isTrial && baseStoresAmount > 0 && (
                        <>
                          <Flex justify="space-between" align="center">
                            <Badge
                              variant="light"
                              color="green.6"
                              c="green.9"
                              leftSection={<IconCheck size={12} />}
                              size="sm"
                              tt="none"
                              style={{ borderRadius: 4, height: 20, fontSize: 12 }}
                            >
                              Pro-rated for the remaining days this month
                            </Badge>
                            <Text size="sm" c="green.9" fw={700}>
                              − R{storesProratedDiscount.toFixed(2)}
                            </Text>
                          </Flex>
                          <Flex justify="end">
                            <Text size="sm" fw={700} c="dark.6">
                              R{storesTotalCalc.toFixed(2)}
                            </Text>
                          </Flex>
                        </>
                      )}
                    </Stack>
                  </Box>
                )}
              </Box>{" "}
            </>
          )}
        </>
      )}

      {showSubscriptionOnly && (
        <>
          <Divider my="md" />
          <Flex justify="space-between" align="center">
            <Text size="md" fw={700} c="dark.9">
              Estimated total
            </Text>
            <Flex align="baseline" gap={4}>
              <Text size="sm" c="gray.6">
                Excl VAT
              </Text>
              <Text size="lg" fw={700} c="dark.9">
                R{estimatedTotalExclVAT.toFixed(2)}
              </Text>
            </Flex>
          </Flex>
        </>
      )}

      {!showSubscriptionOnly && (
        <>
          {!showUsageOnly && <Box className={styles.divider} />}

          {/* Storage */}
          <Box className={styles.section}>
            <Flex align="center" justify="space-between" className={styles.header}>
              <Flex className={styles.infoTitle}>
                <Text size={"sm"} fw={700}>
                  Total storage used
                </Text>
                {storageDiscountPercentageVal > 0 && (
                    <Badge
                        variant="light"
                        color="green.6"
                        c="green.9"
                        leftSection={<IconCheck size={12} />}
                        size="sm"
                        tt="none"
                        style={{ borderRadius: 4, height: 20, fontSize: 12 }}
                        ml="xs"
                    >
                      {storageDiscountPercentageVal}% discount applied{storageRateCard?.DiscountEndDate ? ` until ${moment(storageRateCard.DiscountEndDate).format("D MMM YYYY")}` : ""}
                    </Badge>
                )}
                <Tooltip
                  color={"scBlue"}
                  label="This includes all your uploaded attachments - files, images and documents."
                  withArrow
                >
                  <Box>
                    <IconInfoCircle size={16} color="var(--mantine-color-dark-6)" />
                  </Box>
                </Tooltip>
              </Flex>
            </Flex>

            <UsageProgressBar
              currentValue={storageUsedBytes}
              freeTierValue={storageFreeTierBytes}
              minMaxFallback={storageMinMaxBytes}
              currentValueLabel={formatStorage(storageUsedBytes)}
              freeTierLabel={formatFreeStorageLabel(storageFreeTierBytes)}
            />

            <Flex justify={"end"} align={"center"} h={30} bg={"gray.0"} mt={30}>
              <Text size="sm" fw={400} c="dark.6" mr={"sm"} style={isTrial ? { textDecoration: 'line-through' } : {}}>
                {`${billedStorageGB.toFixed(2)}GB × R${(storageRate * storagePctDiscountFactor).toFixed(2)} = `}
                <Text span fw={700}>
                  R{storageCost.toFixed(2)}
                </Text>
              </Text>
            </Flex>
          </Box>

          <Box className={styles.divider} />

          {/* Emails */}
          <Box className={styles.section}>
            <Flex align="center" justify="space-between" className={styles.header}>
              <Flex className={styles.infoTitle}>
                <Text size={"sm"} fw={700}>
                  Emails this month
                </Text>
                {emailDiscountPercentageVal > 0 && (
                    <Badge
                        variant="light"
                        color="green.6"
                        c="green.9"
                        leftSection={<IconCheck size={12} />}
                        size="sm"
                        tt="none"
                        style={{ borderRadius: 4, height: 20, fontSize: 12 }}
                        ml="xs"
                    >
                      {emailDiscountPercentageVal}% discount applied{emailRateCard?.DiscountEndDate ? ` until ${moment(emailRateCard.DiscountEndDate).format("D MMM YYYY")}` : ""}
                    </Badge>
                )}
                <Tooltip
                  color={"scBlue"}
                  label="This includes all outbound emails."
                  withArrow
                >
                  <Box>
                    <IconInfoCircle size={16} color="var(--mantine-color-dark-6)" />
                  </Box>
                </Tooltip>
              </Flex>
            </Flex>

            <UsageProgressBar
              currentValue={emailsSent}
              freeTierValue={emailsFreeTier}
              minMaxFallback={emailsMinMax}
              currentValueLabel={`${emailsSent.toLocaleString()} emails sent`}
              freeTierLabel={`${emailsFreeTier.toLocaleString()} emails free`}
            />

            <Flex justify={"end"} align={"center"} h={30} bg={"gray.0"} mt={30}>
              <Text size="sm" fw={400} c="dark.6" mr={"sm"} style={isTrial ? { textDecoration: 'line-through' } : {}}>
                {`${billedEmails.toLocaleString()} × R${(emailRate * emailPctDiscountFactor).toFixed(2)} = `}
                <Text span fw={700}>
                  R{emailCost.toFixed(2)}
                </Text>
              </Text>
            </Flex>
          </Box>
        </>
      )}
    </Box>
  );
};

export default UsageSummary;
